import React from 'react';
import { Box, Typography, Grid, MenuItem, Container, useTheme, alpha, Card } from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
import { StyledTextField, StyledSelect } from './index';
import { Settings as SettingsIcon } from '@mui/icons-material';

const currencies = [
  { code: 'USD', label: 'US Dollar ($)', symbol: '$' },
  { code: 'EUR', label: 'Euro (€)', symbol: '€' },
  { code: 'GBP', label: 'British Pound (£)', symbol: '£' },
  { code: 'CAD', label: 'Canadian Dollar (C$)', symbol: 'C$' },
  { code: 'AUD', label: 'Australian Dollar (A$)', symbol: 'A$' }
];

const BusinessSettings = ({ 
  companyInfo, 
  handleInputChange, 
  loading, 
  requiredFields, 
  validationErrors 
}) => {
  const { hasPermission, canAccessPage, canEditPage } = usePermissions();
  const theme = useTheme();
  
  // Permission check for business settings access
  if (!canAccessPage('business_settings')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 6,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 500,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <SettingsIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Business Settings Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              You don't currently have the necessary permissions to access the Business Settings. Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }
  
  const isFieldRequired = (fieldName) => {
    return requiredFields['business-settings']?.includes(fieldName) || false;
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const canEditSettings = canEditPage('business_settings');

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Business Settings
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            maxWidth: '400px'
          }}>
            Configure operational preferences, currency settings, and business defaults
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledSelect
            label="Default Currency"
            name="default_currency"
            value={companyInfo.default_currency}
            onChange={handleInputChange}
            required={isFieldRequired('default_currency')}
            error={!!getFieldError('default_currency')}
            helperText={getFieldError('default_currency')}
            disabled={loading || !canEditSettings}
          >
            {currencies.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Default Payment Terms (days)" 
            name="default_payment_terms"
            type="number"
            value={companyInfo.default_payment_terms} 
            onChange={handleInputChange} 
            required={isFieldRequired('default_payment_terms')}
            error={!!getFieldError('default_payment_terms')}
            helperText={getFieldError('default_payment_terms')}
            disabled={loading || !canEditSettings}
            placeholder="30"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Invoice Prefix" 
            name="invoice_prefix"
            value={companyInfo.invoice_prefix} 
            onChange={handleInputChange} 
            required={isFieldRequired('invoice_prefix')}
            error={!!getFieldError('invoice_prefix')}
            helperText={getFieldError('invoice_prefix')}
            disabled={loading || !canEditSettings}
            placeholder="INV"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Quote Prefix" 
            name="quote_prefix"
            value={companyInfo.quote_prefix} 
            onChange={handleInputChange} 
            required={isFieldRequired('quote_prefix')}
            error={!!getFieldError('quote_prefix')}
            helperText={getFieldError('quote_prefix')}
            disabled={loading || !canEditSettings}
            placeholder="QUO"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessSettings;